import clsx from 'clsx';
import { FadeIn } from 'components/common/FadeIn';
import ToggleButtonRow from 'components/common/ToggleButtonRow/ToggleButtonRow';
import ExpandySection from 'components/ExpandySection';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useState } from 'react';

import styles from './QuickAnswers.module.css';

const QAndA = {
  Popular: [
    {
      question: 'How do so many people win?',
      answer: (
        <p>
          On average, every entry has roughly a 1 in 3 chance of winning
          something (from free entries to trips worth £100k) in every draw. We
          have many more winners in smaller prize categories than big ones; but
          there's nothing more exciting for us than making someone's day with a
          bucket list trip!
        </p>
      ),
    },
    {
      question: 'Can I trust DAYMADE?',
      answer: (
        <>
          <p>
            Yes - DAYMADE is a UK prize-draw company that’s been running since
            2019. But you don’t have to take our word for it:
          </p>
          <ul>
            <li>Read our reviews on Trustpilot (4.5★) and Google (4.8★)</li>
            <li>
              See real winners posted every week on Instagram, @daymadeuk, and
              at daymade.co.uk/winners
            </li>
            <li>Meet the small UK team behind DAYMADE on our About Us page</li>
            <li>
              Look us up on Companies House - search "DAYMADE Ltd." for the full
              public record
            </li>
          </ul>
        </>
      ),
    },
    {
      question: 'How does it work?',
      answer: (
        <p>
          It’s simple: pick your numbers and match to win & choose your prize.
          We draw the winning numbers every Tuesday and Friday at 5pm. The more
          numbers you match, the bigger the prize you can choose.
        </p>
      ),
    },
  ],
  'How it works': [
    {
      question: 'How does it work?',
      answer: (
        <p>
          It’s simple: pick your numbers and match to win & choose your prize.
          We draw the winning numbers every Tuesday and Friday at 5pm. The more
          numbers you match, the bigger the prize you can choose.
        </p>
      ),
    },
    {
      question: 'How will I know if I’ve won?',
      answer: (
        <p>
          We’ll send you an email and show the win in your DAYMADE{' '}
          <Link href="/account">account</Link>. If you haven’t claimed it, we’ll
          remind you - and for a bigger win, we may give you a call if you’ve
          shared your number.
        </p>
      ),
    },
  ],
  Prizes: [
    {
      question: 'What prizes can I win?',
      answer: (
        <p>
          Every entry gives you a chance to play for hundreds of prizes across
          trips, experiences, lifestyle, family and tech. The more numbers you
          match, the bigger the prize you can choose - with prizes ranging from
          little treats to home renovations worth £100,000.{' '}
          <Link href="/prizes">Check out our prizes.</Link>
        </p>
      ),
    },
    {
      question: 'Can I choose my prize?',
      answer: (
        <p>
          Yes - that’s one of the best bits. When you win you can choose a prize
          from the category you’ve won in. For example, if you win a Platinum
          prize, you can choose from all the prizes on offer within the Platinum
          category.
        </p>
      ),
    },
  ],
  Trees: [
    {
      question: 'What charities do you work with?',
      answer: (
        <p>
          For our tree planting, we work with{' '}
          <Link target="_blank" href="https://impact.veritree.com/daymade">
            veritree
          </Link>
          . Through them we support charities such as Earth Lungs and Mother
          Tree. We also work with Ecologi, a UK-based charity who carbon offsets
          the flights of our holiday winners, as well as our team’s carbon
          footprint.
        </p>
      ),
    },
    {
      question: 'Where are the trees planted?',
      answer: (
        <p>
          We work with{' '}
          <Link target="_blank" href="https://impact.veritree.com/daymade">
            veritree
          </Link>{' '}
          who plant verifiable trees donated by DAYMADE players in Kenya,
          Tanzania and Senegal.
        </p>
      ),
    },
  ],
  'View all FAQ': [{ href: '/faq', question: '', answer: '' }],
};

const QuestionsAndAnswers = () => {
  const [selectedQuestions, setSelectedQuestions] =
    useState<keyof typeof QAndA>('Popular');
  const [toggleKey, setToggleKey] = useState(0);
  const toggleItems = Object.keys(QAndA) as (keyof typeof QAndA)[];
  const router = useRouter();

  return (
    <div className={styles.questionsAndAnswers}>
      <FadeIn onScroll className={styles.toggleButtons}>
        <ToggleButtonRow
          key={toggleKey}
          items={toggleItems}
          initialSelectedItem={selectedQuestions}
          onChange={(x) => {
            if (!x) {
              setSelectedQuestions('Popular');
              setToggleKey((k) => k + 1);
              return;
            }

            if ('href' in QAndA[x][0]) {
              router.push(QAndA[x][0].href);
              return;
            }
            setSelectedQuestions(x);
          }}
          withDesktopColumn
        />
      </FadeIn>
      <div className={styles.content}>
        {QAndA[selectedQuestions].map((item, i) => (
          <FadeIn
            onScroll
            delay={0.2 * i}
            duration={0.4}
            inViewAmount={0.2}
            className={clsx(styles.qAndAItem)}
            key={
              // Ensure that the key includes both the index of the question and the selected group of questions, so that
              // expanding the first question on mobile works when switching the selected group of questions
              i + selectedQuestions
            }
          >
            <ExpandySection
              containerClassName={styles.questionExpandyButton}
              expandedContainerClassName={styles.questionExpandyButtonExpanded}
              headerContent={(expanded) => (
                <span className={styles.questionWrapper}>
                  <h3
                    className={clsx(
                      styles.question,
                      expanded
                        ? 'daymadeTypographyMBold'
                        : 'daymadeTypographyMMedium',
                    )}
                  >
                    {item.question}
                  </h3>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="10"
                    height="6"
                    viewBox="0 0 10 6"
                    fill="none"
                  >
                    <path
                      d="M0.667969 4.6665L4.66797 0.666504L8.66797 4.6665"
                      stroke-width="1.33333"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke="#9B9B9B"
                    />
                  </svg>
                </span>
              )}
            >
              <div className={clsx('daymadeTypographyMRegular', styles.answer)}>
                {item.answer}
              </div>
            </ExpandySection>
          </FadeIn>
        ))}
      </div>
    </div>
  );
};

export default QuestionsAndAnswers;
